import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createPaymentIntent, extractApiError } from '../api.js';

function formatEur(cents) {
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function CheckoutForm({ amountCents, onSuccess, onCancel, disabled }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (stripeError) {
        setError(stripeError.message || 'Paiement refusé.');
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        onSuccess();
      } else {
        setError('Paiement en attente de confirmation. Réessayez dans quelques instants.');
      }
    } catch (err) {
      setError(extractApiError(err, 'Paiement impossible.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button onClick={onCancel} disabled={submitting || disabled}>
          Annuler
        </Button>
        <Button type="submit" variant="contained" disabled={!stripe || submitting || disabled}>
          {submitting ? 'Paiement…' : `Payer ${formatEur(amountCents)}`}
        </Button>
      </DialogActions>
    </Box>
  );
}

/** Dialog Stripe Elements pour solder une préréservation repas. */
export default function StripeCheckoutDialog({
  open,
  preregistrationId,
  amountCents,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [intentAmount, setIntentAmount] = useState(amountCents || 0);

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  useEffect(() => {
    if (!open || !preregistrationId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setClientSecret(null);
    setPublishableKey(null);

    createPaymentIntent(preregistrationId)
      .then((data) => {
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setPublishableKey(data.publishable_key);
        setIntentAmount(data.amount_cents);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(extractApiError(err, 'Impossible de préparer le paiement.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, preregistrationId]);

  const elementsOptions = clientSecret
    ? { clientSecret, appearance: { theme: 'stripe' }, locale: 'fr' }
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Paiement par carte bancaire</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Règlement sécurisé via Stripe. Vos tickets seront disponibles immédiatement après validation.
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && stripePromise && elementsOptions && (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <CheckoutForm
              amountCents={intentAmount}
              onSuccess={onSuccess}
              onCancel={onClose}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}
